import MirrorBackButton from "@/components/MirrorBackButton";
import { PeopleTasksLauncher } from "@/components/mirror/PeopleTaskActionModal";
import { ContactActionLauncher } from "@/components/mirror/ContactActionModal";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import PreviewNotice from "@/components/PreviewNotice";
import { Plus, ChevronLeft, AlertCircle, MessageCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/i18n/LanguageProvider";

// ── Types ──────────────────────────────────────────────────────────────────

type ContactFilter = "All" | "Close" | "Work" | "Family" | "Mentors";
type Freshness = "fresh" | "aging" | "stale";

interface Contact {
  id: string;
  name: string;
  initials: string;
  avatarColor: string;
  category: Exclude<ContactFilter, "All">;
  lastContact: string;
  freshness: Freshness;
  notes: string;
  nextFollowUp: string;
}

// ── Static data ────────────────────────────────────────────────────────────

const FILTERS: ContactFilter[] = ["All", "Close", "Work", "Family", "Mentors"];

const CONTACTS: Contact[] = [
  {
    id: "1",
    name: "Marcus Chen",
    initials: "MC",
    avatarColor: "hsl(265 60% 45%)",
    category: "Mentors",
    lastContact: "3 weeks ago",
    freshness: "stale",
    notes: "Career mentor. Last discussed promotion strategy.",
    nextFollowUp: "Overdue",
  },
  {
    id: "2",
    name: "Sarah K.",
    initials: "SK",
    avatarColor: "hsl(142 55% 40%)",
    category: "Close",
    lastContact: "2 days ago",
    freshness: "fresh",
    notes: "Talked about her new project. Supportive energy.",
    nextFollowUp: "This week",
  },
  {
    id: "3",
    name: "Dad",
    initials: "DA",
    avatarColor: "hsl(45 80% 45%)",
    category: "Family",
    lastContact: "1 week ago",
    freshness: "aging",
    notes: "Called on Sunday. He's doing well.",
    nextFollowUp: "This weekend",
  },
  {
    id: "4",
    name: "Ana Costa",
    initials: "AC",
    avatarColor: "hsl(187 60% 40%)",
    category: "Work",
    lastContact: "Yesterday",
    freshness: "fresh",
    notes: "Working on the same project. Daily sync.",
    nextFollowUp: "Tomorrow",
  },
  {
    id: "5",
    name: "James R.",
    initials: "JR",
    avatarColor: "hsl(220 55% 45%)",
    category: "Close",
    lastContact: "5 days ago",
    freshness: "fresh",
    notes: "Friend since college. Need to plan the trip.",
    nextFollowUp: "Next week",
  },
  {
    id: "6",
    name: "Paulo M.",
    initials: "PM",
    avatarColor: "hsl(0 55% 45%)",
    category: "Mentors",
    lastContact: "1 month ago",
    freshness: "stale",
    notes: "Business mentor. Hasn't replied to last message.",
    nextFollowUp: "Overdue",
  },
];

const FRESHNESS_STYLE: Record<Freshness, { bar: string; badge: string; badgeText: string; badgeBorder: string }> = {
  fresh: {
    bar: "hsl(142 55% 45%)",
    badge: "hsl(142 60% 35% / 0.20)",
    badgeText: "hsl(142 55% 55%)",
    badgeBorder: "hsl(142 55% 40% / 0.30)",
  },
  aging: {
    bar: "hsl(45 80% 45%)",
    badge: "hsl(45 80% 40% / 0.18)",
    badgeText: "hsl(45 90% 60%)",
    badgeBorder: "hsl(45 80% 45% / 0.30)",
  },
  stale: {
    bar: "hsl(0 55% 45%)",
    badge: "hsl(0 55% 35% / 0.20)",
    badgeText: "hsl(0 60% 60%)",
    badgeBorder: "hsl(0 55% 40% / 0.30)",
  },
};

const CATEGORY_STYLE: Record<Exclude<ContactFilter, "All">, { bg: string; text: string }> = {
  Close:   { bg: "hsl(187 72% 50% / 0.12)", text: "hsl(187 72% 60%)"  },
  Work:    { bg: "hsl(220 55% 38% / 0.20)", text: "hsl(220 60% 65%)"  },
  Family:  { bg: "hsl(45 80% 40% / 0.18)",  text: "hsl(45 90% 60%)"   },
  Mentors: { bg: "hsl(265 60% 55% / 0.15)", text: "hsl(265 70% 72%)"  },
};

const FRESHNESS_BAR_PCT: Record<Freshness, number> = {
  fresh: 90,
  aging: 55,
  stale: 20,
};

// ── Page ───────────────────────────────────────────────────────────────────

const Contacts: React.FC = () => {
  const navigate  = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [filter, setFilter]     = useState<ContactFilter>("All");
  const [expanded, setExpanded] = useState<string | null>(null);

  const visible = filter === "All" ? CONTACTS : CONTACTS.filter((c) => c.category === filter);

  const stagger = (i: number) => ({ delay: 0.06 + i * 0.08, duration: 0.5 });

  return (
    <MobileLayout className="pb-28 pt-8 relative overflow-hidden">
      <PreviewNotice />
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[380px] h-[220px] rounded-full opacity-[0.04]"
          style={{ background: "radial-gradient(ellipse 55% 50% at 50% 50%, hsl(187 72% 50%), transparent)" }}
        />
      </div>

      {/* BACK BUTTON */}
      <MirrorBackButton className="mb-4" />

      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between mb-5"
      >
        <div>
          <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-0.5"
            style={{ color: "hsl(187 72% 50% / 0.55)" }}>
            {t("contacts.subtitle")}
          </p>
          <h1 className="font-display text-[26px] font-semibold text-foreground leading-none">
            {t("contacts.title")}
          </h1>
        </div>
        <button
          onClick={() => toast({ description: t("contacts.toast.addComingSoon"), duration: 2000 })}
          className="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300"
          style={{ background: "hsl(187 72% 50% / 0.10)", border: "1px solid hsl(187 72% 50% / 0.20)" }}
        >
          <Plus size={16} style={{ color: "hsl(187 72% 50%)" }} />
        </button>
      </motion.div>

      <ContactActionLauncher />
      <PeopleTasksLauncher kind="contact" nativeContactsOnly />

      {/* MIRROR ALERT */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(1)}
        className="rounded-2xl p-4 mb-5 relative overflow-hidden"
        style={{ background: "hsl(265 60% 40% / 0.12)", border: "1px solid hsl(265 60% 55% / 0.35)" }}
      >
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[hsl(265_60%_55%)] to-transparent opacity-20" />
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
            style={{ background: "hsl(187 72% 50% / 0.12)", border: "1px solid hsl(187 72% 50% / 0.20)" }}>
            <AlertCircle size={14} style={{ color: "hsl(187 72% 55%)" }} />
          </div>
          <div className="flex-1">
            <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-1"
              style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("contacts.mirrorAlert")}</p>
            <p className="text-[12px] font-body leading-relaxed mb-3" style={{ color: "hsl(210 20% 65%)" }}>
              {t("contacts.mirrorAlertText")}
            </p>
            <button
              onClick={() => {
                setFilter("Mentors");
                toast({ description: t("ux.preview.unavailable"), duration: 2000 });
              }}
              className="flex items-center gap-1 font-display text-[11px] font-medium tracking-wide opacity-75 hover:opacity-100 transition-opacity"
              style={{ color: "hsl(187 72% 55%)" }}
            >
              <span>{t("contacts.remindMe")}</span>
            </button>
          </div>
        </div>
      </motion.div>

      {/* FILTERS */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={stagger(1.5)}
        className="flex gap-2 mb-5 overflow-x-auto pb-1"
        style={{ scrollbarWidth: "none" }}
      >
        {FILTERS.map((f) => {
          const active = filter === f;
          return (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className="shrink-0 px-4 py-1.5 rounded-full font-display text-[11px] tracking-wide transition-all duration-200 active:scale-95"
              style={
                active
                  ? { background: "hsl(var(--primary))", color: "hsl(var(--background))", fontWeight: 600 }
                  : { background: "hsl(187 72% 50% / 0.07)", border: "1px solid hsl(187 72% 50% / 0.15)", color: "hsl(210 20% 60%)" }
              }
            >
              {t(`contacts.filter.${f.toLowerCase()}`)}
            </button>
          );
        })}
      </motion.div>

      {/* CONTACT LIST */}
      <div className="space-y-2.5 mb-5">
        <AnimatePresence>
          {visible.map((c, i) => {
            const fs = FRESHNESS_STYLE[c.freshness];
            const cat = CATEGORY_STYLE[c.category];
            const isOpen = expanded === c.id;
            return (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.04 * i }}
                className="rounded-2xl overflow-hidden"
                style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.18)" }}
              >
                <button
                  onClick={() => setExpanded(isOpen ? null : c.id)}
                  className="w-full flex items-center gap-3 p-4 text-left active:opacity-80 transition-opacity"
                >
                  {/* Avatar */}
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 font-display text-[13px] font-bold text-white"
                    style={{ background: c.avatarColor }}
                  >
                    {c.initials}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <p className="font-display text-[13px] font-semibold text-foreground">{c.name}</p>
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-display tracking-wide"
                        style={{ background: cat.bg, color: cat.text }}>
                        {t(`contacts.filter.${c.category.toLowerCase()}`)}
                      </span>
                    </div>
                    <p className="text-[10px] font-body mb-1.5" style={{ color: "hsl(218 14% 50%)" }}>
                      {t("contacts.last")} {c.lastContact}
                    </p>
                    {/* Proximity bar */}
                    <div className="h-1 rounded-full overflow-hidden"
                      style={{ background: "hsl(218 14% 20%)", width: "80%" }}>
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${FRESHNESS_BAR_PCT[c.freshness]}%`, background: fs.bar }}
                      />
                    </div>
                  </div>

                  <span className="text-[9px] font-display px-2 py-0.5 rounded-full shrink-0"
                    style={{ background: fs.badge, color: fs.badgeText, border: `1px solid ${fs.badgeBorder}` }}>
                    {t(`contacts.${c.freshness}`)}
                  </span>
                </button>

                {/* Expanded */}
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 border-t"
                        style={{ borderColor: "hsl(187 72% 50% / 0.12)" }}>
                        <p className="text-[9px] font-display tracking-[0.14em] uppercase mt-3 mb-1"
                          style={{ color: "hsl(218 14% 40%)" }}>{t("contacts.personalNotes")}</p>
                        <p className="text-[11px] font-body leading-relaxed mb-3"
                          style={{ color: "hsl(210 20% 60%)" }}>{c.notes}</p>

                        <p className="text-[9px] font-display tracking-[0.14em] uppercase mb-1"
                          style={{ color: "hsl(218 14% 40%)" }}>{t("contacts.nextFollowUp")}</p>
                        <p className="text-[11px] font-display font-semibold mb-3"
                          style={{ color: c.freshness === "stale" ? "hsl(0 60% 60%)" : "hsl(187 72% 60%)" }}>
                          {c.nextFollowUp}
                        </p>

                        <button
                          onClick={() => navigate("/mirror")}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-xl font-display text-[11px] font-medium tracking-wide transition-all duration-200 active:scale-[0.97]"
                          style={{ background: "hsl(187 72% 50% / 0.10)", border: "1px solid hsl(187 72% 50% / 0.22)", color: "hsl(187 72% 60%)" }}
                        >
                          <MessageCircle size={12} />
                          {t("contacts.prepareConversation")}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* IMPORT */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(5)}
        className="flex flex-col items-center gap-1.5 mb-2"
      >
        <button disabled
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-display text-[12px] font-medium tracking-wide opacity-35 cursor-not-allowed"
          style={{ border: "1px solid hsl(187 72% 50% / 0.30)", color: "hsl(187 72% 50%)" }}>
          {t("contacts.importContacts")}
        </button>
        <p className="text-[10px] font-display tracking-wide" style={{ color: "hsl(218 14% 40%)" }}>
          {t("contacts.comingSoon")}
        </p>
      </motion.div>

    </MobileLayout>
  );
};

export default Contacts;
